import { Fragment, useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, type ApiError } from '../../api/client';
import { Alert } from '../../components/Field';
import { Tile } from '../../components/Tile';
import { useAuth } from '../../auth/AuthContext';

interface Customer {
  id: string;
  name: string;
  slug: string;
  status: string;
  users: number;
  departments: number;
  createdAt: string;
}

/** The slug is part of a sign-in URL, so it is derived, not typed. */
function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120);
}

function day(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString();
}

/**
 * Customer organizations.
 *
 * The last screen in the navigation that was still a placeholder. The API has
 * been there since Milestone 6 — list, create and update — so this is the UI
 * catching up with it rather than new capability.
 *
 * Creating a customer optionally creates its first administrator in the same
 * step, because an organization nobody can sign into is not usable, and making
 * that a second screen is how it gets forgotten.
 */
export function Customers() {
  const { can } = useAuth();
  const [rows, setRows] = useState<Customer[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  // The customer whose suspension is awaiting confirmation, if any.
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    adminEmail: '',
    adminFirstName: '',
    adminLastName: '',
  });

  const load = useCallback(() => {
    api<{ items: Customer[] }>('/admin/customers')
      .then((result) => setRows(result.items))
      .catch((caught) => setError(caught as ApiError));
  }, []);

  useEffect(load, [load]);

  async function create(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // Only send the administrator fields when an address was given: the API
      // rejects an empty string where it expects an email.
      const payload: Record<string, string> = {
        name: form.name,
        slug: form.slug || slugify(form.name),
      };
      if (form.adminEmail.trim()) {
        payload.adminEmail = form.adminEmail.trim();
        if (form.adminFirstName.trim()) payload.adminFirstName = form.adminFirstName.trim();
        if (form.adminLastName.trim()) payload.adminLastName = form.adminLastName.trim();
      }

      await api('/admin/customers', { method: 'POST', body: JSON.stringify(payload) });
      setNotice(`${form.name} was created.`);
      setForm({ name: '', slug: '', adminEmail: '', adminFirstName: '', adminLastName: '' });
      setCreating(false);
      load();
    } catch (caught) {
      setError(caught as ApiError);
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(customer: Customer, status: string) {
    setError(null);
    setConfirmingId(null);
    try {
      await api(`/admin/customers/${customer.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      setNotice(`${customer.name} is now ${status.toLowerCase()}.`);
      load();
    } catch (caught) {
      setError(caught as ApiError);
    }
  }

  const totals = rows
    ? {
        customers: rows.length,
        active: rows.filter((row) => row.status === 'ACTIVE').length,
        people: rows.reduce((sum, row) => sum + row.users, 0),
        departments: rows.reduce((sum, row) => sum + row.departments, 0),
      }
    : null;

  return (
    <>
      <div className="page__head">
        <h1 className="page__title">Clients</h1>
        <p className="page__lede">
          The companies that use SkillXzen. Each client is kept separate: its people, structure and
          reporting are sealed off from every other client.
        </p>
      </div>

      {error ? <Alert kind="error" title={error.message} items={error.details} /> : null}
      {notice ? <Alert kind="success" title={notice} /> : null}

      <div className="tiles">
        <Tile
          tone="navy"
          icon="⬢"
          label="Clients"
          value={totals ? String(totals.customers) : '—'}
          note={totals ? `${totals.active} active` : undefined}
        />
        <Tile
          tone="azure"
          icon="◍"
          label="People"
          value={totals ? totals.people.toLocaleString() : '—'}
          note="across all clients"
        />
        <Tile
          tone="indigo"
          icon="⊞"
          label="Departments"
          value={totals ? String(totals.departments) : '—'}
          note="structure defined"
        />
      </div>

      <div className="card">
        <div className="card__head">
          <span className="card__title">Client companies</span>
          {can('CUSTOMER_CREATE') ? (
            <button className="btn btn--sm" onClick={() => setCreating((value) => !value)}>
              {creating ? 'Cancel' : 'New client'}
            </button>
          ) : null}
        </div>

        {creating ? (
          <form className="card__body" onSubmit={create} noValidate>
            <div className="field">
              <label className="field__label" htmlFor="cx-name">
                Client name
              </label>
              <input
                className="field__input"
                id="cx-name"
                required
                value={form.name}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    name: event.target.value,
                    // Follows the name until someone edits it themselves.
                    slug:
                      current.slug === slugify(current.name)
                        ? slugify(event.target.value)
                        : current.slug,
                  }))
                }
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="cx-slug">
                Slug
              </label>
              <input
                className="field__input"
                id="cx-slug"
                value={form.slug}
                placeholder={slugify(form.name) || 'acme-engineering'}
                onChange={(event) => setForm((c) => ({ ...c, slug: event.target.value }))}
                aria-describedby="cx-slug-hint"
              />
              <p className="field__hint" id="cx-slug-hint">
                Lowercase letters, digits and single hyphens. Used to tell organizations apart at
                sign-in.
              </p>
            </div>

            <p className="muted" style={{ marginBottom: 'var(--sx-space-3)' }}>
              Optionally create the first administrator now. Without one, nobody can sign in to this
              organization.
            </p>

            <div className="field">
              <label className="field__label" htmlFor="cx-admin">
                Administrator email
              </label>
              <input
                className="field__input"
                id="cx-admin"
                type="email"
                value={form.adminEmail}
                onChange={(event) => setForm((c) => ({ ...c, adminEmail: event.target.value }))}
              />
            </div>

            <div className="field__row">
              <div className="field" style={{ flex: 1 }}>
                <label className="field__label" htmlFor="cx-first">
                  First name
                </label>
                <input
                  className="field__input"
                  id="cx-first"
                  value={form.adminFirstName}
                  onChange={(event) =>
                    setForm((c) => ({ ...c, adminFirstName: event.target.value }))
                  }
                />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label className="field__label" htmlFor="cx-last">
                  Last name
                </label>
                <input
                  className="field__input"
                  id="cx-last"
                  value={form.adminLastName}
                  onChange={(event) =>
                    setForm((c) => ({ ...c, adminLastName: event.target.value }))
                  }
                />
              </div>
            </div>

            <button className="btn" type="submit" disabled={busy || !form.name.trim()}>
              {busy ? 'Creating…' : 'Create client'}
            </button>
          </form>
        ) : null}

        {!rows ? (
          <div className="empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="empty">
            No clients yet.
            <br />
            <span className="muted">Create one to give an organization its own tenant.</span>
          </div>
        ) : (
          <div className="tablewrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Slug</th>
                  <th>People</th>
                  <th>Departments</th>
                  <th>Status</th>
                  <th>Client since</th>
                  {can('CUSTOMER_UPDATE') ? <th>Actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <Fragment key={row.id}>
                    <tr>
                      <td>
                        <strong>{row.name}</strong>
                      </td>
                      <td>
                        <code className="mono">{row.slug}</code>
                      </td>
                      <td>{row.users.toLocaleString()}</td>
                      <td>{row.departments}</td>
                      <td>
                        <span
                          className={`pill pill--${row.status === 'ACTIVE' ? 'approved' : 'pending'}`}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td>{day(row.createdAt)}</td>
                      {can('CUSTOMER_UPDATE') ? (
                        <td>
                          {/* Suspending cuts off a whole organization, so it asks first.
                            Reactivating only restores access and goes straight through. */}
                          <button
                            className="iconbtn"
                            disabled={confirmingId === row.id}
                            onClick={() =>
                              row.status === 'ACTIVE'
                                ? setConfirmingId(row.id)
                                : void setStatus(row, 'ACTIVE')
                            }
                          >
                            {row.status === 'ACTIVE' ? 'Suspend' : 'Reactivate'}
                          </button>
                        </td>
                      ) : null}
                    </tr>
                    {confirmingId === row.id ? (
                      <tr>
                        <td colSpan={7}>
                          <div role="alertdialog" aria-label={`Suspend ${row.name}?`}>
                            <Alert
                              kind="warning"
                              title={`Suspend ${row.name}?`}
                              items={[
                                `All ${row.users.toLocaleString()} ${row.users === 1 ? 'person' : 'people'} in this organization will be signed out immediately.`,
                                'They will not be able to sign in or open any course until the organization is reactivated.',
                                'Nothing is deleted — reactivating restores access.',
                              ]}
                            />
                            <div className="certcard__actions">
                              <button
                                className="btn btn--sm btn--danger"
                                onClick={() => void setStatus(row, 'SUSPENDED')}
                              >
                                Suspend {row.name}
                              </button>
                              <button
                                className="btn btn--sm btn--ghost"
                                onClick={() => setConfirmingId(null)}
                                autoFocus
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
